import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { FormField } from '@re/form-engine';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { usePlatformLookups } from '../../api/lookups';
import type { FormSummary } from '../../api/types';
import { Button } from '../../components/button';
import { ErrorState, Loading } from '../../components/common';
import { FormPreviewPane } from './FormPreviewPane';
import { isEn, tr } from '../../lib/i18n';

type PreviewLocationState = { fields?: FormField[] } | null;

export function FormPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const loc = useLocation();
  const stateFields = (loc.state as PreviewLocationState)?.fields;
  const lookups = usePlatformLookups();

  const forms = useQuery({ queryKey: ['forms'], queryFn: () => api<{ items: FormSummary[] }>('/forms') });
  const form = forms.data?.items.find((f) => f.id === id);

  const draftQ = useQuery({
    queryKey: ['form-draft', id],
    queryFn: async () => {
      try {
        return await api<{ fields: FormField[] }>(`/forms/${id}/draft`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !stateFields,
  });

  const publishedQ = useQuery({
    queryKey: ['form-version', form?.published_version_id],
    queryFn: () => api<{ fields: FormField[] }>(`/forms/versions/${form!.published_version_id}`),
    enabled: !stateFields && !draftQ.data && !!form?.published_version_id,
  });

  const fields = stateFields ?? draftQ.data?.fields ?? publishedQ.data?.fields;
  const loading = forms.isLoading || lookups.isLoading || (!stateFields && (draftQ.isLoading || publishedQ.isLoading));
  const error = draftQ.isError ? draftQ.error : publishedQ.isError ? publishedQ.error : null;

  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={() => { draftQ.refetch(); publishedQ.refetch(); }} />;
  if (!fields?.length || !lookups.data) {
    return (
      <>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Button startIcon={isEn() ? <ArrowBackIcon /> : <ArrowForwardIcon />} onClick={() => nav(`/forms/${id}`)}>{tr('رجوع للمنشئ', 'Back to builder')}</Button>
          <Typography variant="h5">{tr('معاينة النموذج', 'Form preview')}</Typography>
        </Stack>
        <Typography color="text.secondary">{tr('لا توجد حقول للمعاينة. أنشئ مسودة أو انشر نسخة أولاً.', 'No fields to preview. Create a draft or publish a version first.')}</Typography>
      </>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={isEn() ? <ArrowBackIcon /> : <ArrowForwardIcon />} onClick={() => nav(`/forms/${id}`)}>{tr('رجوع للمنشئ', 'Back to builder')}</Button>
        <Typography variant="h5" sx={{ flex: 1, minWidth: 0 }} noWrap>{form?.name ?? tr('النموذج', 'Form')}</Typography>
        {stateFields && <Typography variant="caption" color="warning.main">{tr('معاينة التعديلات غير المحفوظة', 'Previewing unsaved changes')}</Typography>}
      </Stack>
      <Card sx={{ maxWidth: 960, mx: 'auto' }}>
        <CardContent>
          <FormPreviewPane phone={false} fields={fields} lookups={lookups.data} />
        </CardContent>
      </Card>
    </>
  );
}

/** AppBar action on the builder page. */
export function openFormPreview(nav: ReturnType<typeof useNavigate>, formId: string, fields: FormField[]) {
  nav(`/forms/${formId}/preview`, { state: { fields } });
}
